import { useOutletContext } from "react-router-dom";
import { Check } from "lucide-react";
import { NextGenLayoutContext } from "~/components/ManifoldNextGenLayout";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import type { ResolvedTheme } from "~/hooks/useNextGenTheme";
import { cn } from "~/lib/utils";
import { SettingsGroup } from "~/pages/manifold-nextgen/components/settings/SettingsGroup";
import { SettingsPage } from "~/pages/manifold-nextgen/components/settings/SettingsPage";
import { SettingsRow } from "~/pages/manifold-nextgen/components/settings/SettingsRow";
import {
  useFigureTheme,
  type FigureThemeChoice,
} from "~/pages/manifold-nextgen/hooks/useFigureTheme";

interface Choice<T extends string> {
  value: T;
  label: string;
}

// A settings row's dropdown, labelled by the row it sits in. Also where the one
// cast from the widget's string back to the choice type lives.
const ChoiceSelect = <T extends string>({
  label,
  value,
  choices,
  onChange,
}: {
  label: string;
  value: T;
  choices: Choice<T>[];
  onChange: (choice: T) => void;
}) => (
  <Select value={value} onValueChange={(next) => onChange(next as T)}>
    <SelectTrigger className="w-56" aria-label={label}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {choices.map((choice) => (
        <SelectItem key={choice.value} value={choice.value}>
          {choice.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

// "single" means whichever of light/dark is picked below, not a mode of its own.
type ThemeModeChoice = "single" | "system";

const THEME_MODE_CHOICES: Choice<ThemeModeChoice>[] = [
  { value: "single", label: "Single theme" },
  { value: "system", label: "Sync with system" },
];

// Named after the box above rather than "system" or "auto": the choice is to
// follow the setting on this same page, which is not the OS setting.
const PLOT_THEME_CHOICES: Choice<FigureThemeChoice>[] = [
  { value: "app", label: "Match main appearance" },
  { value: "light", label: "Always light" },
  { value: "dark", label: "Always dark" },
];

// Colors come from `.mds-swatch-*`, which pins each theme's surfaces so both
// previews stay readable whichever theme is active.
const SWATCH: Record<ResolvedTheme, string> = {
  light: "mds-swatch-light",
  dark: "mds-swatch-dark",
};

const ThemePreview = ({ theme }: { theme: ResolvedTheme }) => (
  <div
    className={cn("flex h-24 gap-1.5 bg-[var(--mds-swatch-surface)] p-2", SWATCH[theme])}
    aria-hidden="true"
  >
    <div className="w-1/4 rounded bg-[var(--mds-swatch-chrome)]" />
    <div className="flex flex-1 flex-col gap-1.5">
      <div className="h-2 w-3/4 rounded-full bg-[var(--mds-swatch-line)]" />
      <div className="h-2 w-full rounded-full bg-[var(--mds-swatch-line)]" />
      <div className="h-2 w-2/3 rounded-full bg-[var(--mds-swatch-line)]" />
    </div>
  </div>
);

const THEMES: { theme: ResolvedTheme; label: string }[] = [
  { theme: "light", label: "Light" },
  { theme: "dark", label: "Dark" },
];

/** The light and dark previews: pickable normally, status while syncing. */
const ThemeCards = ({
  resolvedTheme,
  isSyncing,
  onPick,
}: {
  resolvedTheme: ResolvedTheme;
  isSyncing: boolean;
  onPick: (theme: ResolvedTheme) => void;
}) => (
  <div
    className="grid grid-cols-2 gap-4 p-4"
    role={isSyncing ? undefined : "radiogroup"}
    aria-label={isSyncing ? undefined : "Theme"}
  >
    {THEMES.map(({ theme, label }) => {
      const isActive = resolvedTheme === theme;
      const card = (
        <>
          <ThemePreview theme={theme} />
          <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
            <span className="text-sm font-medium text-foreground">{label}</span>
            {isSyncing
              ? isActive && <Badge variant="secondary">Active</Badge>
              : isActive && <Check className="size-4 text-primary" />}
          </div>
        </>
      );

      // While syncing the themes are status, not choices.
      return isSyncing ? (
        <div
          key={theme}
          role="group"
          aria-label={label}
          className={cn(
            "overflow-hidden rounded-lg border",
            isActive ? "border-primary" : "border-border opacity-60"
          )}
        >
          {card}
        </div>
      ) : (
        <button
          key={theme}
          type="button"
          role="radio"
          aria-checked={isActive}
          onClick={() => onPick(theme)}
          className={cn(
            "cursor-pointer overflow-hidden rounded-lg border text-left transition-colors",
            "focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none",
            isActive ? "border-primary" : "border-border hover:border-primary/50"
          )}
        >
          {card}
        </button>
      );
    })}
  </div>
);

/**
 * The profile's Appearance tab: the platform theme, the theme plots render in,
 * and the way back to the classic interface. Platform theme state lives on the
 * layout, which hands it down through the profile shell's `Outlet`.
 */
export const ProfileAppearanceTab = () => {
  const { themeMode, resolvedTheme, setThemeMode, onSwitchToClassic } =
    useOutletContext<NextGenLayoutContext>();
  const { figureThemeChoice, setFigureThemeChoice } = useFigureTheme();

  const isSyncing = themeMode === "system";

  // Leaving sync pins the resolved theme, so appearance doesn't jump.
  const onChoiceChange = (choice: ThemeModeChoice) =>
    setThemeMode(choice === "system" ? "system" : resolvedTheme);

  return (
    <SettingsPage title="Appearance" description="Choose how Manifold looks for you.">
      <SettingsGroup label="Main appearance">
        <SettingsRow
          title="Theme mode"
          description={
            isSyncing
              ? "Automatically match your system settings."
              : "Use a single theme across the platform."
          }
        >
          <ChoiceSelect
            label="Theme mode"
            value={isSyncing ? "system" : "single"}
            choices={THEME_MODE_CHOICES}
            onChange={onChoiceChange}
          />
        </SettingsRow>
        <ThemeCards resolvedTheme={resolvedTheme} isSyncing={isSyncing} onPick={setThemeMode} />
      </SettingsGroup>

      <SettingsGroup label="Plots">
        <SettingsRow
          title="Plot theme"
          description="Applies to plots the assistant draws. Some set their own colors and will only partly follow this."
        >
          <ChoiceSelect
            label="Plot theme"
            value={figureThemeChoice}
            choices={PLOT_THEME_CHOICES}
            onChange={setFigureThemeChoice}
          />
        </SettingsRow>
      </SettingsGroup>

      {/* Its own box, with a destructive button: classic is the previous version of
          the platform, not a skin. */}
      {onSwitchToClassic && (
        <SettingsGroup label="Classic look">
          <SettingsRow title="Switch back to the previous Manifold interface.">
            <Button variant="destructive" onClick={onSwitchToClassic}>
              Switch to classic look
            </Button>
          </SettingsRow>
        </SettingsGroup>
      )}
    </SettingsPage>
  );
};
